import {Trans} from '@lingui/macro';
import {ellipsis} from 'polished';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {textSelection} from '../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {
  getCalendarEventReminders,
  makeCalendarEventVersionColor,
} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {htmlToText} from '../../../../srcCommon/services/converters/htmlToTextConverter';
import {TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {CalendarEventModel, CalendarEventStatusesEnum} from '../../../models/api/calendarEventModel';
import {
  CalendarEventVersionModel,
  CalendarEventVersionVisibilityTypesEnum,
} from '../../../models/api/calendarEventVersionModel';
import {CalendarVersionAccessRolesEnum, CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {CalendarEventData} from '../../../store/panels/calendars/calendarsPanelSelectors';
import {CalendarsPanelId} from '../../../store/panels/panelStateBase';
import {CalendarEventTimeFormatter} from '../../common/formatters/calendarEventTimeFormatter';
import {LinkifyFormatter} from '../../common/formatters/markdown/linkifyFormatter';
import {LinkTrap} from '../../common/linkTrap/linkTrap';
import {LinkTooltips} from '../../common/tooltips/linkTooltips';
import {MessageHtml} from '../../inboxes/viewer/activity/message/messageHtml';
import {buildCalendarEventDescriptionHtml} from '../calendarEventHelpers';
import {ConferenceToken} from '../editor/conferenceEditor/conferenceToken';
import {CalendarEventParticipantsList} from '../participants/calendarEventParticipantsList';
import {CalendarEventParticipantsSummary} from '../participants/calendarEventParticipantsSummary';
import {CalendarRemindersViewer} from '../reminders/calendarRemindersViewer';
import {CalendarEventDetailRow} from './calendarEventDetailRow';

/*
 * Props.
 */

export interface CalendarEventViewerContentProps {
  panelId: CalendarsPanelId;
  calendarEventData: CalendarEventData;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  timeFormat: TimeFormatsEnum;
}

/*
 * Style.
 */

interface EventColorStyleProps {
  $color: PaletteColorsEnum;
}

const StyledEventColorDiv = styled.div<EventColorStyleProps>`
  background-color: ${(p) => p.theme.palette[p.$color].shade40};
  height: 12px;
  width: 12px;
  border-radius: 3px;
  margin-top: 11px;
`;

const StyledSummaryWrapperDiv = styled.div`
  margin-bottom: 4px;
`;

interface SummaryStyleProps {
  $isCancelled: boolean;
}
const StyledSummaryDiv = styled.div<SummaryStyleProps>`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.semibold};
  line-height: 28px;
  text-decoration: ${(p) => (p.$isCancelled ? 'line-through' : 'none')};
  ${textSelection()}
`;

const StyledTextDiv = styled.div`
  line-height: 21px;
  white-space: pre-wrap;
  min-width: 0px;
  overflow: hidden;
  ${textSelection()}

  p {
    margin: 0px;
  }

  a {
    color: ${(p) => p.theme.palette[PaletteColorsEnum.BLUE].shade40};
    cursor: pointer;

    &:hover {
      color: ${(p) => p.theme.palette[PaletteColorsEnum.BLUE].shade50};
    }
  }
`;

const StyledCalendarNameDiv = styled.div`
  ${ellipsis()}
`;

const StyledVisibilityContentDiv = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 3px;
  align-items: center;
`;

/*
 * Component.
 */

export const CalendarEventViewerContent: FC<CalendarEventViewerContentProps> = (props) => {
  const {calendarEventData, visualParticipants, timeFormat, panelId} = props;
  const {calendarEventVersion, calendarEvent, calendarVersion} = calendarEventData;
  const {channel, instanceStartDate} = calendarEventData;

  const {description} = calendarEventVersion;
  const channelType = channel ? channel.type : undefined;

  const descriptionHtml = useMemo(
    () => buildCalendarEventDescriptionHtml(description, channelType),
    [description, channelType],
  );
  const descriptionIsEmpty = useMemo(() => htmlToText(description || '').trim().length === 0, [description]);

  return (
    <>
      <CalendarEventDetailRow
        iconContent={renderTitleSectionIcon(calendarEventVersion, calendarVersion)}
        content={renderSummarySection(calendarEventVersion, calendarEvent, calendarVersion)}
      />
      <CalendarEventDetailRow
        iconName="snoozeSmall"
        content={
          <StyledTextDiv>
            <CalendarEventTimeFormatter
              timeFormat={timeFormat}
              calendarEventVersion={calendarEventVersion}
              instanceStartDate={instanceStartDate}
              shouldUseMultiline
            />
          </StyledTextDiv>
        }
      />
      {maybeRenderLocationsRow(calendarEventVersion)}
      {maybeRenderConferenceRow(calendarEventVersion)}
      {maybeRenderParticipantsRow(panelId, visualParticipants)}
      {maybeRenderDescriptionRow(descriptionHtml, descriptionIsEmpty)}
      {maybeRenderRemindersRow(calendarVersion, calendarEventVersion)}
      {maybeRenderVisibilityRow(calendarEventVersion)}
      <CalendarEventDetailRow
        iconName="calendarSmall"
        content={renderCalendarContent(props.calendarEventData)}
      />
    </>
  );
};

function renderTitleSectionIcon(
  calendarEventVersion: CalendarEventVersionModel,
  calendarVersion: CalendarVersionModel,
) {
  const eventColor = makeCalendarEventVersionColor(calendarEventVersion, calendarVersion);
  return <StyledEventColorDiv $color={eventColor} />;
}

function renderSummarySection(
  calendarEventVersion: CalendarEventVersionModel,
  calendarEvent: CalendarEventModel,
  calendarVersion: CalendarVersionModel,
) {
  return (
    <StyledSummaryWrapperDiv>
      <StyledSummaryDiv $isCancelled={calendarEvent.status === CalendarEventStatusesEnum.CANCELLED}>
        {renderSummary(calendarEventVersion, calendarVersion)}
      </StyledSummaryDiv>
    </StyledSummaryWrapperDiv>
  );
}

function renderSummary(
  calendarEventVersion: CalendarEventVersionModel,
  calendarVersion: CalendarVersionModel,
) {
  if (calendarVersion.accessRole === CalendarVersionAccessRolesEnum.FREE_BUSY) {
    return <Trans id="calendar-event-viewer-free-busy-event">Busy</Trans>;
  }

  if (
    calendarEventVersion.visibility === CalendarEventVersionVisibilityTypesEnum.PRIVATE &&
    !calendarEventVersion.summary &&
    calendarVersion.accessRole === CalendarVersionAccessRolesEnum.READER
  ) {
    return <Trans id="calendar-event-viewer-private-event">Private event</Trans>;
  }

  if (!calendarEventVersion.summary) {
    return <Trans id="calendar-event-viewer-untitled-event">(Untitled event)</Trans>;
  }

  return calendarEventVersion.summary;
}

function maybeRenderLocationsRow(calendarEventVersion: CalendarEventVersionModel) {
  if (calendarEventVersion.locations.length === 0) {
    return null;
  }

  const locationsContent = (
    <StyledTextDiv>
      <LinkifyFormatter
        text={calendarEventVersion.locations.map((l) => l.displayName).join(', ')}
        onLinkClick={(href) => href && openInBrowser(href)}
      />
    </StyledTextDiv>
  );
  return <CalendarEventDetailRow iconName="locationSmall" content={locationsContent} />;
}

function maybeRenderConferenceRow(calendarEventVersion: CalendarEventVersionModel) {
  if (!calendarEventVersion.conference) {
    return null;
  }

  return (
    <CalendarEventDetailRow
      iconName="videoSmall"
      content={<ConferenceToken conference={calendarEventVersion.conference} />}
    />
  );
}

function maybeRenderParticipantsRow(
  panelId: CalendarsPanelId,
  visualParticipants: ReadonlyArray<VisualParticipantModel>,
) {
  const attendees = visualParticipants.filter((p) => p.isAttendee);
  if (attendees.length === 0) {
    return null;
  }

  return (
    <CalendarEventDetailRow
      iconName="teammatesSmall"
      content={renderParticipantsContent(panelId, attendees)}
    />
  );
}

function renderParticipantsContent(
  panelId: CalendarsPanelId,
  attendees: ReadonlyArray<VisualParticipantModel>,
) {
  return (
    <div>
      <CalendarEventParticipantsSummary visualParticipants={attendees} />
      <CalendarEventParticipantsList visualParticipants={attendees} panelId={panelId} />
    </div>
  );
}

function maybeRenderDescriptionRow(descriptionHtml: string | undefined, descriptionIsEmpty: boolean) {
  if (!descriptionHtml || descriptionIsEmpty) {
    return null;
  }

  const descriptionContent = (
    <StyledTextDiv>
      <LinkTooltips>
        <LinkTrap>
          <MessageHtml html={descriptionHtml} />
        </LinkTrap>
      </LinkTooltips>
    </StyledTextDiv>
  );

  return <CalendarEventDetailRow iconName="notesSmall" content={descriptionContent} />;
}

function maybeRenderRemindersRow(
  calendarVersion: CalendarVersionModel,
  calendarEventVersion: CalendarEventVersionModel,
) {
  const remindersToDisplay = getCalendarEventReminders(calendarVersion, calendarEventVersion);
  if (remindersToDisplay.length === 0) {
    return null;
  }

  return (
    <CalendarEventDetailRow
      iconName="subscribeSmall"
      content={<CalendarRemindersViewer reminders={remindersToDisplay} />}
    />
  );
}

function maybeRenderVisibilityRow(calendarEventVersion: CalendarEventVersionModel) {
  if (calendarEventVersion.visibility !== CalendarEventVersionVisibilityTypesEnum.PRIVATE) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconName="eyeSmall"
          content={
            <StyledVisibilityContentDiv>
              <Trans id="calendar-event-viewer-private">Private</Trans>
              <Icon name="lockFilledTiny" color={theme.greys.shade70} />
            </StyledVisibilityContentDiv>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

function renderCalendarContent(calendarEventData: CalendarEventData) {
  const {calendar, calendarVersion} = calendarEventData;
  return <StyledCalendarNameDiv>{calendarVersion.nameOverride || calendar.name}</StyledCalendarNameDiv>;
}
